import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, Button, Grid, Tab, Tabs } from "@mui/material";
import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DiscountFormControl } from "../../../components/form/DiscountFormControl";
import { ProductSelectionFormControl } from "../../../components/form/ProductSelectionFormControl/ProductSelectionFormControl";
import { STATUS_KEYS } from "../../../schemas/status";
import { AgreementConfigurationDialogMode } from "./AgreementConfigurationDialog";
import { AgreementDetailsFormControl } from "./Tabs/AgreementDetailsFormControl";
import { AgreementKindSelection } from "./Tabs/AgreementKindSelection";
import { AgreementPaymentScheduleTab } from "./Tabs/AgreementPaymentScheduleTab/AgreementPaymentScheduleTab";
import { CustomersDataTab } from "./Tabs/CustomersDataTab";
import { CustomersTab } from "./Tabs/CustomersTab";
import { useIsAgreementFormDataValid } from "./useIsAgreementFormDataValid";

export const AgreementDialogTabType = ((AgreementDialogTabType) => {
    AgreementDialogTabType[(AgreementDialogTabType.AGREEMENT_TYPE = 0)] = "AGREEMENT_TYPE";
    AgreementDialogTabType[(AgreementDialogTabType.CUSTOMERS = 1)] = "CUSTOMERS";
    AgreementDialogTabType[(AgreementDialogTabType.CUSTOMERS_DATA = 2)] = "CUSTOMERS_DATA";
    AgreementDialogTabType[(AgreementDialogTabType.AGREEMENT_DETAILS = 3)] = "AGREEMENT_DETAILS";
    AgreementDialogTabType[(AgreementDialogTabType.PRODUCTS = 4)] = "PRODUCTS";
    AgreementDialogTabType[(AgreementDialogTabType.DISCOUNT = 5)] = "DISCOUNT";
    AgreementDialogTabType[(AgreementDialogTabType.SCHEDULE = 6)] = "SCHEDULE";
    return AgreementDialogTabType;
})({});

export const AgreementDialogTabs = (props) => {
    const { selectedTab, setSelectedTab, control, readonly, mode, fields, parentAgreementKind, investmentId, agreementHasRescheduledPayments } =
        props;
    const { t } = useTranslation();
    const { setValue } = useFormContext();

    const areTabsDisabled = useAreTabsDisabled(fields);
    const isFirstTab = selectedTab === AgreementDialogTabType.AGREEMENT_TYPE;
    const isLastTab = selectedTab === AgreementDialogTabType.SCHEDULE;
    const canGoToNextTab = !isLastTab && !areTabsDisabled[selectedTab + 1];
    const canGoToPreviousTab = !isFirstTab && !areTabsDisabled[selectedTab - 1];

    const handleGoToNextTab = useCallback(() => {
        if (canGoToNextTab) {
            setSelectedTab(selectedTab + 1);
        }
    }, [canGoToNextTab, setSelectedTab, selectedTab]);

    const handleGoToPreviousTab = useCallback(() => {
        if (canGoToPreviousTab) {
            setSelectedTab(selectedTab - 1);
        }
    }, [canGoToPreviousTab, setSelectedTab, selectedTab]);

    const handleTabChange = useCallback(
        (event, newTab) => {
            setSelectedTab(newTab);
        },
        [setSelectedTab],
    );


    const agreementKind = useWatch({ name: fields.agreementKind });
    const agreementType = useWatch({ name: fields.agreementType });

    const setAgreementType = useCallback(
        (type) => {
            setValue(fields.agreementType, type);
        },
        [setValue, fields.agreementType],
    );

    const productDiscountsRaw = useWatch({ name: fields.productDiscounts });
    const productDiscounts = useMemo(() => productDiscountsRaw || {}, [productDiscountsRaw]);
    const selectedProductIds = Object.keys(productDiscounts);
    const payments = useWatch({ name: fields.payments });

    // This needs to update related discounts as well as payments
    const handleSetSelectedProductIds = useCallback(
        (productIds) => {
            const productIdsToAdd = productIds.filter((id) => !selectedProductIds.includes(id));
            const productIdsToRemove = selectedProductIds.filter((id) => !productIds.includes(id));

            // Update discounts
            const updatedDiscounts = { ...productDiscounts };
            for (const productId of productIdsToAdd) {
                updatedDiscounts[productId] = 0;
            }
            for (const productId of productIdsToRemove) {
                delete updatedDiscounts[productId];
            }
            setValue(fields.productDiscounts, updatedDiscounts);

            // Update payments
            const updatedPayments = payments.map((payment) => ({
                ...payment,
                per_product_percentages: [
                    // Remove unselected
                    ...payment.per_product_percentages.filter(
                        (pp) => !productIdsToRemove.includes(pp.product_id),
                    ),
                    // Add newly selected
                    ...productIdsToAdd.map((pid) => ({
                        product_id: pid,
                        percentage: 0,
                    })),
                ],
            }));
            setValue(fields.payments, updatedPayments);
        },
        [
            productDiscounts,
            selectedProductIds,
            setValue,
            fields.productDiscounts,
            fields.payments,
            payments,
        ],
    );

    const setPayments = useCallback(
        (newPayments) => {
            setValue(fields.payments, newPayments);
        },
        [setValue, fields.payments],
    );

    const canModifyAgreementType = ![
        AgreementConfigurationDialogMode.EDIT,
        AgreementConfigurationDialogMode.ANNEX,
        AgreementConfigurationDialogMode.PROLONGATE,
    ].includes(mode);

    const isProlongateMode = mode === AgreementConfigurationDialogMode.PROLONGATE;

    return (
        <Grid container direction="column">
            <Grid item>
                <Tabs value={selectedTab} onChange={handleTabChange}>
                    <Tab
                        label={t("agreements.AgreementDialogTabs.agreementType")}
                        value={AgreementDialogTabType.AGREEMENT_TYPE}
                        disabled={areTabsDisabled[AgreementDialogTabType.AGREEMENT_TYPE]}
                    />
                    <Tab
                        label={t("agreements.AgreementDialogTabs.customers")}
                        value={AgreementDialogTabType.CUSTOMERS}
                        disabled={areTabsDisabled[AgreementDialogTabType.CUSTOMERS]}
                    />
                    <Tab
                        label={t("agreements.AgreementDialogTabs.customersData")}
                        value={AgreementDialogTabType.CUSTOMERS_DATA}
                        disabled={areTabsDisabled[AgreementDialogTabType.CUSTOMERS_DATA]}
                    />
                    <Tab
                        label={t("agreements.AgreementDialogTabs.agreementDetails")}
                        value={AgreementDialogTabType.AGREEMENT_DETAILS}
                        disabled={areTabsDisabled[AgreementDialogTabType.AGREEMENT_DETAILS]}
                    />
                    <Tab
                        label={t("agreements.AgreementDialogTabs.products")}
                        value={AgreementDialogTabType.PRODUCTS}
                        disabled={areTabsDisabled[AgreementDialogTabType.PRODUCTS]}
                    />
                    <Tab
                        label={t("agreements.AgreementDialogTabs.discount")}
                        value={AgreementDialogTabType.DISCOUNT}
                        disabled={areTabsDisabled[AgreementDialogTabType.DISCOUNT]}
                    />
                    <Tab
                        label={t("agreements.AgreementDialogTabs.schedule")}
                        value={AgreementDialogTabType.SCHEDULE}
                        disabled={areTabsDisabled[AgreementDialogTabType.SCHEDULE]}
                    />
                </Tabs>
            </Grid>
            <Grid item sx={{ my: 2 }}>
                <TabPanel activeTab={selectedTab} tab={AgreementDialogTabType.AGREEMENT_TYPE}>
                    <AgreementKindSelection
                        selectedKind={agreementType}
                        setSelectedKind={setAgreementType}
                        parentAgreementKind={parentAgreementKind}
                        readonly={readonly || !canModifyAgreementType}
                    />
                </TabPanel>
                <TabPanel activeTab={selectedTab} tab={AgreementDialogTabType.CUSTOMERS}>
                    <CustomersTab
                        control={control}
                        fieldName={fields.customerIds}
                        readOnly={readonly || isProlongateMode}
                    />
                </TabPanel>
                <TabPanel activeTab={selectedTab} tab={AgreementDialogTabType.CUSTOMERS_DATA}>
                    <CustomersDataTab
                        control={control}
                        fields={{
                            customerIds: fields.customerIds,
                            customerShares: fields.customerShares,
                            customerFlags: fields.customerFlags,
                        }}
                        readOnly={readonly || isProlongateMode}
                    />
                </TabPanel>
                <TabPanel activeTab={selectedTab} tab={AgreementDialogTabType.AGREEMENT_DETAILS}>
                    <AgreementDetailsFormControl
                        fields={fields}
                        readonly={readonly}
                        prolongateMode={isProlongateMode}
                        agreementKind={agreementKind}
                        investmentId={investmentId}
                        productIds={selectedProductIds}
                        productDiscounts={productDiscounts}
                    />
                </TabPanel>
                <TabPanel activeTab={selectedTab} tab={AgreementDialogTabType.PRODUCTS}>
                    <ProductSelectionFormControl
                        multipleApartments={false}
                        selectedProductIds={selectedProductIds}
                        setSelectedProductIds={handleSetSelectedProductIds}
                        readOnly={readonly || isProlongateMode || agreementHasRescheduledPayments}
                        excludedStatuses={[STATUS_KEYS.Sold, STATUS_KEYS.Reservation]}
                    />
                </TabPanel>
                <TabPanel activeTab={selectedTab} tab={AgreementDialogTabType.DISCOUNT}>
                    <DiscountFormControl
                        fields={{ discount: fields.productDiscounts }}
                        readonly={readonly || isProlongateMode || agreementHasRescheduledPayments}
                        showIncreaseRequestButton={false}
                    />
                </TabPanel>
                <TabPanel activeTab={selectedTab} tab={AgreementDialogTabType.SCHEDULE}>
                    <AgreementPaymentScheduleTab
                        payments={payments}
                        setPayments={setPayments}
                        productIds={selectedProductIds}
                        productDiscounts={productDiscounts}
                        readonly={readonly || isProlongateMode || agreementHasRescheduledPayments}
                    />
                </TabPanel>
            </Grid>
            <Grid container justifyContent={"space-between"} gap={2}>
                <Grid item xs>
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<KeyboardArrowLeftIcon />}
                        onClick={handleGoToPreviousTab}
                        disabled={!canGoToPreviousTab}
                    >
                        {t("agreements.AgreementDialogTabs.back")}
                    </Button>
                </Grid>
                <Grid item xs>
                    <Button
                        variant="contained"
                        fullWidth
                        endIcon={<KeyboardArrowRightIcon />}
                        onClick={handleGoToNextTab}
                        disabled={!canGoToNextTab}
                    >
                        {t("agreements.AgreementDialogTabs.continue")}
                    </Button>
                </Grid>
            </Grid>
        </Grid>
    );
};

const TabPanel = (props) => {
    const { children, activeTab, tab } = props;

    return activeTab === tab ? <Box>{children}</Box> : null;
};

const useAreTabsDisabled = (fields) => {
    const validation = useIsAgreementFormDataValid(fields);

    const isAgreementTypeTabDisabled = false; // First tab is never disabled
    const isCustomersTabDisabled = !validation.isAgreementTypeValid;
    const isCustomersDataTabDisabled =
        isCustomersTabDisabled || !validation.isCustomerSelectionValid;
    const isAgreementDetailsTabDisabled =
        isAgreementTypeTabDisabled || !validation.isCustomerInfoValid;
    const isProductsTabDisabled =
        isAgreementTypeTabDisabled || !validation.areAgreementDetailsValid;
    const isDiscountTabDisabled = isProductsTabDisabled || !validation.isProductSelectionValid;
    const isScheduleTabDisabled = isDiscountTabDisabled || !validation.isProductSelectionValid;

    return [
        isAgreementTypeTabDisabled,
        isCustomersTabDisabled,
        isCustomersDataTabDisabled,
        isAgreementDetailsTabDisabled,
        isProductsTabDisabled,
        isDiscountTabDisabled,
        isScheduleTabDisabled,
    ];
};
